"use client";
import { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Icons
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>;
const LoaderIcon = () => <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>;

export default function PayslipsPage() {
    const [payrollData, setPayrollData] = useState<any[]>([]);
    const [companies, setCompanies] = useState<any[]>([]);
    const [periods, setPeriods] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);

    const [filters, setFilters] = useState({
        companyId: '',
        periodId: '',
        departmentId: '',
        search: ''
    });

    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState<string | null>(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [compRes, perRes, deptRes] = await Promise.all([
                    fetch('/api/companies'),
                    fetch('/api/payroll-periods'),
                    fetch('/api/departments')
                ]);
                setCompanies(await compRes.json());
                const periodsData = await perRes.json();
                setPeriods(periodsData);
                setDepartments(await deptRes.json());

                if (periodsData.length > 0) {
                    setFilters(prev => ({ ...prev, periodId: periodsData[0].id.toString() }));
                }
            } catch (error) {
                console.error("Failed to load filters", error);
            }
        };
        fetchInitialData();
    }, []);

    const fetchPayroll = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.periodId) params.append('periodId', filters.periodId);
            if (filters.companyId) params.append('companyId', filters.companyId);

            const res = await fetch(`/api/payroll?${params.toString()}`);
            let data = await res.json();

            if (filters.departmentId) {
                data = data.filter((p: any) => p.department_id?.toString() === filters.departmentId);
            }
            if (filters.search) {
                const s = filters.search.toLowerCase();
                data = data.filter((p: any) =>
                    p.employee_name.toLowerCase().includes(s) ||
                    p.employee_id.toLowerCase().includes(s)
                );
            }

            setPayrollData(data);
        } catch (error) {
            console.error("Failed to fetch payroll", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (filters.periodId) fetchPayroll();
    }, [filters]);

    const numberToWords = (num: number) => {
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const format = (n: number): string => {
            if (n < 20) return a[n];
            const digit = n % 10;
            if (digit === 0) return b[Math.floor(n / 10)];
            return b[Math.floor(n / 10)] + ' ' + a[digit];
        };

        const convert = (n: number): string => {
            if (n === 0) return 'Zero';
            let res = '';
            if (n >= 10000000) { res += format(Math.floor(n / 10000000)) + 'Crore '; n %= 10000000; }
            if (n >= 100000) { res += format(Math.floor(n / 100000)) + 'Lakh '; n %= 100000; }
            if (n >= 1000) { res += format(Math.floor(n / 1000)) + 'Thousand '; n %= 1000; }
            if (n >= 100) { res += format(Math.floor(n / 100)) + 'Hundred '; n %= 100; }
            if (n > 0) {
                if (res !== '') res += 'and ';
                res += format(n);
            }
            return res.trim();
        };

        const total = Math.floor(num);
        const paise = Math.round((num - total) * 100);
        let result = convert(total) + ' Rupees';
        if (paise > 0) result += ' and ' + convert(paise) + ' Paise';
        return result + ' Only';
    };

    const createPayslipPDF = (doc: jsPDF, record: any) => {
        const formatCurrency = (val: any) => {
            const num = parseFloat(val);
            if (isNaN(num)) return '0.00';
            return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        };

        // Set global settings
        doc.setTextColor(0, 0, 0);

        // 1. Logo and Header
        try {
            // Static path to public/logo.png for jsPDF
            // Placing logo top left, or centered? User said "everything in black... and no logo" earlier but then asked for it again.
            // I'll place it slightly to the left of the centered text or at the top left.
            doc.addImage('/logo.png', 'PNG', 14, 14, 17, 17);
        } catch (e) {
            console.warn("Logo not found at /logo.png");
        }

        // Main Header - Company Full Name (Centered)
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        const companyName = record.company_full_name || record.company || "Manikanta Group";
        doc.text(companyName.toUpperCase(), 105, 20, { align: 'center' });

        // Sub-Header - Payslip for the month of BOLD_MONTH BOLD_YEAR (Centered)
        const prefix = "Payslip for the month of ";
        const monthYear = `${record.period_month} ${record.period_year}`;

        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        const prefixWidth = doc.getTextWidth(prefix);
        doc.setFont("helvetica", "bold");
        const monthYearWidth = doc.getTextWidth(monthYear);
        const totalHeaderWidth = prefixWidth + monthYearWidth;
        const startX = 105 - (totalHeaderWidth / 2);

        doc.setFont("helvetica", "normal");
        doc.text(prefix, startX, 28);
        doc.setFont("helvetica", "bold");
        doc.text(monthYear, startX + prefixWidth, 28);

        // 2. Employee Info Table (Side by Side - 4 Columns)
        autoTable(doc, {
            startY: 38,
            theme: 'grid',
            body: [
                [
                    { content: 'Employee Name', styles: { fontStyle: 'bold', cellWidth: 35 } },
                    { content: record.employee_name, styles: { fontStyle: 'bold', cellWidth: 67 } },
                    { content: 'Employee ID', styles: { fontStyle: 'bold', cellWidth: 30 } },
                    { content: record.employee_id, styles: { fontStyle: 'bold', cellWidth: 50 } }
                ],
                [
                    { content: 'Department', styles: { fontStyle: 'bold' } },
                    { content: record.department },
                    { content: 'Designation', styles: { fontStyle: 'bold' } },
                    { content: record.designation }
                ],
                [
                    { content: 'Company', styles: { fontStyle: 'bold' } },
                    { content: record.company },
                    { content: 'Month', styles: { fontStyle: 'bold' } },
                    { content: `${record.period_month?.substring(0, 3)} ${record.period_year}`, styles: { fontStyle: 'bold' } }
                ]
            ],
            styles: { fontSize: 10, textColor: [0, 0, 0], cellPadding: 2, lineWidth: 0.25, lineColor: [0, 0, 0] },
        });

        // 3. Attendance Details Table
        const attY = (doc as any).lastAutoTable.finalY + 6;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Attendance Details", 14, attY);

        autoTable(doc, {
            startY: attY + 4,
            head: [['Total Days', 'Paid Days', 'Weekly Holidays', 'OT/Arrears Days', 'Salary Days']],
            body: [[record.total_days, record.paid_days, record.weekly_holidays, record.ot_arrears_days, record.salary_days]],
            theme: 'grid',
            styles: { fontSize: 10, textColor: [0, 0, 0], cellPadding: 2, lineWidth: 0.25, lineColor: [0, 0, 0], halign: 'center' },
            headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: "bold" },
            columnStyles: { 4: { fontStyle: 'bold' } }
        });

        // 4. Earnings & Deductions Tables (Side by Side)
        const columnsY = (doc as any).lastAutoTable.finalY + 6;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Salary Details", 14, columnsY);

        // Earnings Table (Left)
        autoTable(doc, {
            startY: columnsY + 4,
            margin: { right: 107 },
            head: [['Earnings', 'Amount']],
            body: [
                ['Basic Salary', formatCurrency(record.earned_basic)],
                ['HRA', formatCurrency(record.earned_hra)],
                ['Conveyance', formatCurrency(record.earned_conveyance)],
                ['Medical Allowance', formatCurrency(record.earned_medical)],
                ['Special Allowance', formatCurrency(record.earned_special_allowance)],
                ['OT & Arrears', formatCurrency(record.ot_arrears_amount)],
                ['Gross Earnings (A)', formatCurrency(record.gross_salary)]
            ],
            theme: 'grid',
            styles: { fontSize: 10, textColor: [0, 0, 0], cellPadding: 1.5, lineWidth: 0.25, lineColor: [0, 0, 0] },
            headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: "bold", halign: 'center' },
            columnStyles: { 1: { halign: 'right' } },
            didParseCell: function (data) {
                if (data.row.index === 6 && data.section === 'body') {
                    data.cell.styles.fillColor = [200, 200, 200];
                    data.cell.styles.fontStyle = 'bold';
                }
            }
        });

        // Deductions Table (Right)
        autoTable(doc, {
            startY: columnsY + 4,
            margin: { left: 107 },
            head: [['Deductions', 'Amount']],
            body: [
                ['EPF', formatCurrency(record.epf)],
                ['Professional Tax', formatCurrency(record.pt)],
                ['ESI', formatCurrency(record.esi)],
                ['LWF', formatCurrency(record.lwf)],
                ['Advance', formatCurrency(record.advance)],
                ['Canteen', formatCurrency(record.canteen)],
                ['Total Deductions (B)', formatCurrency(record.total_deductions)]
            ],
            theme: 'grid',
            styles: { fontSize: 10, textColor: [0, 0, 0], cellPadding: 1.5, lineWidth: 0.25, lineColor: [0, 0, 0] },
            headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: "bold", halign: 'center' },
            columnStyles: { 1: { halign: 'right' } },
            didParseCell: function (data) {
                if (data.row.index === 6 && data.section === 'body') {
                    data.cell.styles.fillColor = [200, 200, 200];
                    data.cell.styles.fontStyle = 'bold';
                }
            }
        });

        // 5. Final Net Pay Table (Left Aligned, Less Spacing)
        const netPayY = (doc as any).lastAutoTable.finalY + 6;
        autoTable(doc, {
            startY: netPayY,
            theme: 'grid',
            margin: { right: 80 }, // This pulls it effectively to the left by limiting width
            body: [
                [
                    { content: 'Net Salary (A - B)', styles: { fontStyle: 'bold', fillColor: [230, 230, 230] } },
                    { content: `Rs. ${formatCurrency(record.net_pay)}`, styles: { fontStyle: 'bold', halign: 'left' } }
                ],
                [
                    { content: 'Amount in words', styles: { fontStyle: 'bold', fillColor: [230, 230, 230] } },
                    { content: numberToWords(record.net_pay || 0) }
                ]
            ],
            styles: { fontSize: 10, textColor: [0, 0, 0], cellPadding: 2.5, lineWidth: 0.25, lineColor: [0, 0, 0] },
            columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 132 } }
        });

        doc.setFontSize(8);
        doc.text("(Note: 5% retention of Rs. " + formatCurrency(record.retention_amount) + " has been applied)", 14, (doc as any).lastAutoTable.finalY + 6);


    };

    const generatePDF = async (record: any) => {
        setGenerating(record.employee_id);
        try {
            const doc = new jsPDF('p', 'mm', 'a4');
            createPayslipPDF(doc, record);
            const fileName = `${record.employee_id}_${record.employee_name.replace(/\s+/g, '_')}_${record.period.replace(/\s+/g, '_')}_payslip.pdf`;
            doc.save(fileName);
        } catch (error) {
            console.error("PDF Error:", error);
            alert("Failed to generate PDF");
        } finally {
            setGenerating(null);
        }
    };

    const generateAll = async () => {
        if (!confirm(`Generate ${payrollData.length} payslips?`)) return;
        setGenerating('all');
        try {
            for (const record of payrollData) {
                const doc = new jsPDF('p', 'mm', 'a4');
                createPayslipPDF(doc, record);
                const fileName = `${record.employee_id}_${record.employee_name.replace(/\s+/g, '_')}_${record.period.replace(/\s+/g, '_')}_payslip.pdf`;
                doc.save(fileName);
                await new Promise(r => setTimeout(r, 100)); // Small pause to prevent browser throttle
            }
        } catch (e) {
            console.error("Bulk PDF Error:", e);
        } finally {
            setGenerating(null);
        }
    };

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto min-h-screen pb-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Payslip Management</h1>
                    <p className="text-slate-500 mt-1">Generate and distribute employee payslips</p>
                </div>
                <button
                    onClick={generateAll}
                    disabled={payrollData.length === 0 || generating !== null}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 disabled:opacity-50"
                >
                    {generating === 'all' ? <LoaderIcon /> : <DownloadIcon />}
                    Download All ({payrollData.length})
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-200 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">Period</label>
                    <select
                        value={filters.periodId}
                        onChange={e => setFilters(f => ({ ...f, periodId: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    >
                        {periods.map(p => <option key={p.id} value={p.id}>{p.month} {p.year}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">Company</label>
                    <select
                        value={filters.companyId}
                        onChange={e => setFilters(f => ({ ...f, companyId: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    >
                        <option value="">All Companies</option>
                        {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">Department</label>
                    <select
                        value={filters.departmentId}
                        onChange={e => setFilters(f => ({ ...f, departmentId: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    >
                        <option value="">All Departments</option>
                        {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.department_name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">Search Employee</label>
                    <input
                        placeholder="Name or ID..."
                        value={filters.search}
                        onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition"
                    />
                </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="p-20 flex justify-center"><div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div></div>
                ) : payrollData.length === 0 ? (
                    <div className="p-20 text-center text-slate-400 uppercase font-bold tracking-widest">No data available for this selection</div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold tracking-widest border-b border-slate-100">
                            <tr>
                                <th className="px-8 py-4">Employee</th>
                                <th className="px-6 py-4">Structure</th>
                                <th className="px-6 py-4 text-right">Net Pay</th>
                                <th className="px-8 py-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {payrollData.map((row) => (
                                <tr key={`${row.employee_id}-${row.period_id}`} className="hover:bg-slate-50/50 transition duration-200 group">
                                    <td className="px-8 py-5">
                                        <div className="font-bold text-slate-800">{row.employee_name}</div>
                                        <div className="text-xs text-slate-400 font-mono mt-0.5">#{row.employee_id} • {row.company}</div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="text-sm text-slate-600 font-medium">{row.designation}</div>
                                        <div className="text-xs text-slate-400 mt-0.5">{row.department}</div>
                                    </td>
                                    <td className="px-6 py-5 text-right font-bold text-slate-700">
                                        ₹ {row.net_pay?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <button
                                            onClick={() => generatePDF(row)}
                                            disabled={generating !== null}
                                            className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-4 py-2 rounded-xl font-bold text-sm hover:bg-indigo-600 hover:text-white transition duration-200 disabled:opacity-50"
                                        >
                                            {generating === row.employee_id ? <LoaderIcon /> : <DownloadIcon />}
                                            {generating === row.employee_id ? 'Generating...' : 'PDF'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
